import React, { Dispatch } from 'react';
import { Stack } from '@mui/material';
import { QuestionLibraryState, QuestionLibraryAction } from '../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { GenericTableAction } from '../../Components/GenericTable/genericTableReducer';
import QuestionsTableRow from './QuestionsTableRow';
import { headerStyle, candidatesActionsListItem } from '../config';
import { TQuestion } from '../types';
import Search from '../../SmartForms/Search';
import GenericDialog from '../../Components/Modals/GenericDialog';
import EmptyFolder from '../../../NewUI/EditJob/Questions/Modals/Icons/EmptyFolder.svg';
import { styles } from '../styles';

export default function QuestionLibrary({
  QuestionState,
  QuestionDispatch
}: {
  QuestionState: QuestionLibraryState;
  QuestionDispatch: Dispatch<QuestionLibraryAction>;
}) {
  const {
    data: questions,
    isLoading: loadingQuestions,
    refetch: refetchQuestions
  } = useQuery({
    queryKey: ['questions', QuestionState.questionsTableSettings, QuestionState.searchQuery],
    queryFn: async () =>
      await Api.getQuestions({
        page: QuestionState.questionsTableSettings.page,
        limit: QuestionState.questionsTableSettings.limit,
        sort: QuestionState.questionsTableSettings.sort,
        sort_order: QuestionState.questionsTableSettings.sort_order,
        search: QuestionState.searchQuery
      }),
    onError: () =>
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting question library data`,
          state: 'error'
        }
      })
  });

  const { mutate: deleteQuestion, isLoading: deletingQuestion } = useMutation({
    mutationFn: (id: string) => Api.deleteQuestion(id),
    onSuccess: () => {
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Question deleted successfully', state: 'success' }
      });
      QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' });
      refetchQuestions();
    },
    onError: () => {
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'There was an error deleting the question', state: 'error' }
      });
    }
  });

  return (
    <Stack sx={{ rowGap: 2, paddingTop: 5 }}>
      <Search
        idLabel="question-library"
        placeholder="Search question by name"
        setSearch={(title) => QuestionDispatch({ type: 'SET_SEARCH_QUERY', payload: title })}
      />
      <GenericTable
        label="Question library list"
        items={questions?.res?.library_fields || []}
        isLoadingItems={loadingQuestions}
        tableState={QuestionState.questionsTableState}
        dispatch={QuestionDispatch as Dispatch<GenericTableAction<TQuestion>>}
        headerStyle={headerStyle}
        paginationState={QuestionState.questionsTableSettings}
        pagination={{
          totalItems: Number(questions?.resHead['x-total-count']) || 0,
          totalPages: Number(questions?.resHead['x-total-pages']) || 0
        }}
        TableRows={QuestionsTableRow}
        TableActionList={candidatesActionsListItem}
        allowMultipleSelection={false}
        titleCellId="type"
        emptyMessageOverride={
          !questions?.res?.library_fields.length &&
          !QuestionState.searchQuery && (
            <Stack sx={styles.questionLibraryModalEmptyStateContainer}>
              <Stack component="img" src={EmptyFolder} sx={{ width: '8rem' }} />
              <Stack sx={styles.questionLibraryModalEmptyStateMessage}>
                No questions available in the library
              </Stack>
            </Stack>
          )
        }
      />
      <GenericDialog
        isDialogOpen={QuestionState.questionsTableState.actions.isOpen.delete}
        setDialogOpen={() => QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' })}
        title="Permanently delete question?"
        description={
          <Stack>
            <Stack>Are you sure you want to delete this question?</Stack>
            <Stack>This action cannot be undone.</Stack>
          </Stack>
        }
        buttonCallback={() => {
          const item = QuestionState.questionsTableState.actions.selectedItem;
          if (item) {
            deleteQuestion(item.id.toString());
          }
        }}
        callbackLoading={deletingQuestion}
        buttonText="Yes, delete"
        secondaryButtonText="Back to editing"
        url=""
      />
    </Stack>
  );
}
